import React, { useState, useEffect } from 'react';
import { XIcon } from 'lucide-react';
import { NativeDateInput, NativeTimeInput } from '../ui/NativeDateInput';
import { MobileSelect } from '../ui/MobileSelect';
import { toast } from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { getThemesForSport } from '../../utils/sportUtils';
import type { Practice, Team } from '../../types';

interface PracticeFormProps {
  onClose: () => void;
  editingPractice: Practice | null;
  currentTeam: Team | null;
  onSuccess: (practice?: Practice) => void;
}

// Helper type for form state
interface PracticeFormState {
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  theme: string;
}

export function PracticeForm({ onClose, editingPractice, currentTeam, onSuccess }: PracticeFormProps) {
  // Get themes for the current team's sport
  const practiceThemes = getThemesForSport(currentTeam?.sport);
  
  // Create a helper function to set practice form defaults
  const getDefaultPracticeForm = (): PracticeFormState => ({
    date: '',
    startTime: currentTeam?.defaultPracticeStartTime || '15:00',
    endTime: currentTeam?.defaultPracticeEndTime || '17:00',
    location: currentTeam?.defaultPracticeLocation || 'Main Field',
    theme: 'Team Day'
  });

  // Initialize form with default values or editing practice
  const [formState, setFormState] = useState<PracticeFormState>(
    editingPractice ? {
      date: editingPractice.date,
      startTime: editingPractice.start_time,
      endTime: editingPractice.end_time,
      location: editingPractice.location,
      theme: editingPractice.theme || 'Team Day'
    } : getDefaultPracticeForm()
  );
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Update form when editing practice or team changes
  useEffect(() => {
    if (editingPractice) {
      // If editing a practice, use its values
      setFormState({
        date: editingPractice.date,
        startTime: editingPractice.start_time,
        endTime: editingPractice.end_time,
        location: editingPractice.location,
        theme: editingPractice.theme || 'Team Day'
      });
    } else if (currentTeam) {
      // If adding a new practice, use team defaults
      setFormState(prevState => ({
        ...prevState,
        startTime: currentTeam.defaultPracticeStartTime || prevState.startTime || '15:00',
        endTime: currentTeam.defaultPracticeEndTime || prevState.endTime || '17:00',
        location: currentTeam.defaultPracticeLocation || prevState.location || 'Main Field'
      }));
    }
  }, [editingPractice, currentTeam]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentTeam?.id) {
      toast.error('No team selected');
      return;
    }
    
    setIsSubmitting(true);

    // Fix date handling to prevent timezone shifts
    // Ensure the date stays in the local timezone and doesn't get converted
    const localDate = new Date(formState.date + 'T00:00:00'); // Add time to prevent timezone conversion
    const formattedDate = localDate.toISOString().split('T')[0]; // Get YYYY-MM-DD format

    const practiceData = {
      team_id: currentTeam.id,
      date: formattedDate, // Use the properly formatted date
      start_time: formState.startTime,
      end_time: formState.endTime,
      location: formState.location,
      theme: formState.theme
    };

    console.log('Original form date:', formState.date);
    console.log('Formatted date being saved:', formattedDate);

    try {
      if (editingPractice) {
        // For updating practices, get the updated data too
        const { data, error } = await supabase
          .from('practices')
          .update(practiceData)
          .eq('id', editingPractice.id)
          .select(); // Important: Select the updated data

        if (error) {
          console.error('Error updating practice:', error);
          toast.error('Failed to update practice');
          return;
        }
        
        console.log('Practice updated:', data);
        
        // Clear any cache for this team to ensure changes are reflected
        if (currentTeam?.id) {
          // Dispatch a cache clear event to notify all components
          const cacheClearEvent = new CustomEvent('clear-team-cache', {
            detail: { teamId: currentTeam.id }
          });
          window.dispatchEvent(cacheClearEvent);
          
          // Clear any local storage cache
          const cacheKey = `practices_${currentTeam.id}`;
          localStorage.removeItem(cacheKey);
        }

        toast.success('Practice updated successfully');
        onSuccess(data[0]); // Pass the updated practice
      } else {
        // For new practices, we need to get the created practice data
        const { data, error } = await supabase
          .from('practices')
          .insert([practiceData])
          .select(); // Important: Select the created data

        if (error) {
          console.error('Error adding practice:', error);
          toast.error('Failed to create practice');
          return;
        }

        console.log('New practice created:', data);
        
        // Clear any cache for this team
        if (currentTeam?.id) {
          // First, dispatch a cache clear event to notify all components
          const cacheClearEvent = new CustomEvent('clear-team-cache', {
            detail: { teamId: currentTeam.id }
          });
          window.dispatchEvent(cacheClearEvent);
          
          // Then ensure any local storage cache is cleared
          const cacheKey = `practices_${currentTeam.id}`;
          localStorage.removeItem(cacheKey);
        }

        toast.success('Practice created successfully');
        onSuccess(data[0]); // Pass the newly created practice
      }

      // Reset form and close
      onClose();
    } catch (error) {
      console.error('Error in practice form submit:', error);
      toast.error('Failed to save practice');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg p-6 max-w-2xl w-full shadow-xl">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">
          {editingPractice ? 'Edit Practice' : 'Add New Practice'}
        </h2>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700"
        >
          <XIcon size={24} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="practice-theme">Theme</label>
          <MobileSelect
            id="practice-theme"
            aria-label="Theme"
            value={formState.theme}
            onChange={(v) => setFormState({ ...formState, theme: v })}
            options={Object.values(practiceThemes).map((theme) => ({ value: theme, label: theme }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            sheetTitle="Practice Theme"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="practice-date">Date</label>
          <NativeDateInput
            id="practice-date"
            aria-label="Date"
            value={formState.date}
            onChange={(e) => setFormState({ ...formState, date: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="practice-start-time">Start Time</label>
          <NativeTimeInput
            id="practice-start-time"
            aria-label="Start Time"
            value={formState.startTime}
            onChange={(e) => setFormState({ ...formState, startTime: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="practice-end-time">End Time</label>
          <NativeTimeInput
            id="practice-end-time"
            aria-label="End Time"
            value={formState.endTime}
            onChange={(e) => setFormState({ ...formState, endTime: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="practice-location">Location</label>
          <input
            id="practice-location"
            type="text"
            aria-label="Location"
            value={formState.location}
            onChange={(e) => setFormState({ ...formState, location: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            required
          />
        </div>

        <div className="flex justify-end gap-4 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary flex items-center gap-2"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary flex items-center gap-2"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : editingPractice ? 'Update Practice' : 'Add Practice'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default PracticeForm; 